import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { isInoculated } from "@brewbase/lib";
import { TankControlLinks } from "@/features/fermentation-tank-control/tank-control-links";
import { z } from "zod";
import { trpc, trpcClient } from "@/hooks/trpc";
import {
  fermentationGroupId,
  FermentationGroupId,
  FermentationProcedureId,
  FermentationStatusEnum,
} from "@brewbase/db/types";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircleIcon, AlertTriangleIcon, Milk } from "lucide-react";
import { FermentationStatusEnum as StatusEnum } from "@brewbase/db/types";
import { JOURNAL_SECTIONS } from "@/features/fermentation-journal/constants";
import type { GroupWithAllDetails } from "@/features/fermentation-journal/types";
import {
  StatusBadge,
  ProcedureTasksNav,
} from "@/features/fermentation-journal";
import { EftDisplay } from "@/features/fermentation-journal/components/timeline/eft-display";
import { UnitPreferencesProvider } from "@/features/fermentation-journal/hooks/use-unit-preferences";
import { useMemo } from "react";
import { EndRunDialog } from "@/features/fermentation-journal/components/end-run-dialog";
import { useEndRunStore } from "@/features/fermentation-journal/hooks/use-end-run-store";
import { useSidebarConfig } from "@/hooks/use-sidebar-config";
import { HealthSidebar } from "@/features/tank-board/health-controls";
import { LiveReadout } from "@/features/fermentation-readout/live-readout";
import type { SidebarNavItem } from "@/components/nav/sidebar-store";

export const Route = createFileRoute("/fermentations/$group_id")({
  component: JournalLayout,
  params: z.object({
    group_id: z.coerce.number(),
  }),
  loader: async ({ params }) => {
    const group = await trpcClient.fermentations.groups.get.query(
      params.group_id as FermentationGroupId,
    );
    return { groupName: group?.name };
  },
  head: ({ loaderData, params }) => ({
    meta: [
      {
        title: `BrewBase - ${loaderData?.groupName ?? `Fermentation Group ${params.group_id}`}`,
      },
    ],
  }),
});

function JournalLayout() {
  const groupId = Route.useParams().group_id as FermentationGroupId;

  const {
    data: group,
    isLoading,
    error,
  } = trpc.fermentations.groups.getWithAllDetails.useQuery(groupId);

  const utils = trpc.useUtils();
  const transitionMutation = trpc.fermentations.transitionStatus.useMutation({
    onSuccess: () => {
      utils.fermentations.groups.getWithAllDetails.invalidate(groupId);
    },
  });

  const handleStatusTransition = (
    fermentationId: number,
    newStatus: FermentationStatusEnum,
    force?: boolean,
  ) => {
    transitionMutation.mutate({
      id: fermentationId as never,
      status: newStatus,
      force,
      skipTaskValidation: force,
    });
  };

  const transitionAsync = (
    fermentationId: number,
    newStatus: FermentationStatusEnum,
    force?: boolean,
    stopRun?: boolean,
  ) =>
    transitionMutation.mutateAsync({
      id: fermentationId as never,
      status: newStatus,
      force,
      skipTaskValidation: force,
      stopRun,
    });

  if (isLoading) {
    return <JournalSkeleton />;
  }

  if (error || !group) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <AlertCircleIcon className="w-10 h-10 mx-auto text-destructive mb-2" />
          <p className="text-sm text-muted-foreground">
            {error?.message ?? "Failed to load fermentation group"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <JournalContent
      group={group}
      onStatusTransition={handleStatusTransition}
      transitionAsync={transitionAsync}
    />
  );
}

function JournalContent({
  group,
  onStatusTransition,
  transitionAsync,
}: {
  group: GroupWithAllDetails;
  onStatusTransition: (
    id: number,
    status: FermentationStatusEnum,
    force?: boolean,
  ) => void;
  transitionAsync: (
    id: number,
    status: FermentationStatusEnum,
    force?: boolean,
    stopRun?: boolean,
  ) => Promise<unknown>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const fermentations = group.fermentations;
  const currentStatus = fermentations[0]?.status ?? StatusEnum.PLANNED;
  // Every transition to ENDED goes through the end-of-run checkpoint (outcome,
  // terminal incidents, investigation), wherever it was triggered from.
  const endRunOpen = useEndRunStore((s) => s.open);
  const setEndRunOpen = useEndRunStore((s) => s.setOpen);

  const { data: localProtocols } = trpc.fermentations.protocols.list.useQuery({
    groupId: group.id,
  });

  const pathParts = location.pathname.split("/");
  const activeSection = pathParts[3] ?? "planning";

  // Calculate EFT
  const startedAt = fermentations[0]?.started_at;
  const endedAt = fermentations[0]?.ended_at;
  // Before inoculation `started_at` holds the row's insert time, not a start:
  // there is no fermentation time to show yet.
  const eft =
    startedAt && isInoculated(currentStatus)
      ? calculateEft(
          new Date(startedAt),
          endedAt ? new Date(endedAt) : null,
          currentStatus,
        )
      : null;

  // Calculate task summary
  const taskSummary = useMemo(() => {
    let total = 0;
    let completed = 0;
    let pending = 0;
    let inProgress = 0;

    // Also aggregate byStatus across all fermentations
    const byStatus: Record<
      FermentationStatusEnum,
      { total: number; completed: number }
    > = {
      [FermentationStatusEnum.PLANNED]: { total: 0, completed: 0 },
      [FermentationStatusEnum.PREPARING]: { total: 0, completed: 0 },
      [FermentationStatusEnum.PREPARED]: { total: 0, completed: 0 },
      [FermentationStatusEnum.RUNNING]: { total: 0, completed: 0 },
      [FermentationStatusEnum.ENDED]: { total: 0, completed: 0 },
      [FermentationStatusEnum.ARCHIVED]: { total: 0, completed: 0 },
    };

    for (const [_fermId, summary] of Object.entries(
      group.taskSummaries ?? {},
    )) {
      if (summary) {
        total += summary.total ?? 0;
        completed += summary.completed ?? 0;
        pending += summary.pending ?? 0;
        inProgress += summary.inProgress ?? 0;

        // Aggregate byStatus
        for (const [status, counts] of Object.entries(summary.byStatus ?? {})) {
          const statusKey = status as FermentationStatusEnum;
          byStatus[statusKey].total += counts.total ?? 0;
          byStatus[statusKey].completed += counts.completed ?? 0;
        }
      }
    }

    return { total, completed, pending, inProgress, byStatus };
  }, [group.taskSummaries]);

  // Determine the next transition status
  const nextStatus = useMemo(() => {
    const transitions: Record<
      FermentationStatusEnum,
      FermentationStatusEnum | null
    > = {
      [FermentationStatusEnum.PLANNED]: FermentationStatusEnum.PREPARING,
      [FermentationStatusEnum.PREPARING]: FermentationStatusEnum.PREPARED,
      [FermentationStatusEnum.PREPARED]: FermentationStatusEnum.RUNNING,
      [FermentationStatusEnum.RUNNING]: FermentationStatusEnum.ENDED,
      [FermentationStatusEnum.ENDED]: FermentationStatusEnum.ARCHIVED,
      [FermentationStatusEnum.ARCHIVED]: null,
    };
    return transitions[currentStatus] ?? null;
  }, [currentStatus]);

  // Only block if there are incomplete tasks for the NEXT status
  const incompleteTasks = nextStatus
    ? taskSummary.byStatus[nextStatus].total -
      taskSummary.byStatus[nextStatus].completed
    : 0;
  const transitionBlocked = incompleteTasks > 0;
  const transitionBlockReason = transitionBlocked
    ? `${incompleteTasks} task${incompleteTasks > 1 ? "s" : ""} remaining for ${nextStatus}`
    : undefined;

  // Filter visible sections and build navigation
  const navigation = useMemo((): SidebarNavItem[] => {
    const visibleSections = JOURNAL_SECTIONS.filter((section) =>
      section.visibleStatuses.includes(currentStatus),
    );

    const items: SidebarNavItem[] = visibleSections.map(
      (section): SidebarNavItem => {
        const visibleChildren =
          section.children?.filter(
            (child) => !child.condition || child.condition(group),
          ) ?? [];

        if (visibleChildren.length > 0) {
          return {
            type: "section",
            id: section.id,
            label: section.label,
            icon: section.icon,
            children: visibleChildren.map((child) => ({
              type: "link" as const,
              id: child.id,
              label:
                child.id === "protocol" && (localProtocols?.length ?? 0) >= 1
                  ? "Protocols"
                  : child.label,
              href: `/fermentations/${group.id}/${section.id}/${child.id}`,
            })),
          };
        }

        return {
          type: "link",
          id: section.id,
          label: section.label,
          icon: section.icon,
          href: `/fermentations/${group.id}/${section.id}`,
        };
      },
    );

    items.push({
      type: "custom",
      id: "procedure-tasks",
      content: (
        <ProcedureTasksNav
          procedureId={
            (group.procedure_id as FermentationProcedureId | null) ?? null
          }
          groupId={group.id}
          currentStatus={currentStatus}
        />
      ),
    });

    return items;
  }, [currentStatus, group, activeSection, localProtocols]);

  // Stable identity so the readout's polling is not restarted by a render.
  const readoutTanks = useMemo(
    () =>
      fermentations.map((f) => ({
        id: f.id,
        name: f.name ?? `F-${f.id}`,
        colorCode: f.color_code ? String(f.color_code) : null,
      })),
    [fermentations],
  );

  const controlTanks = useMemo(
    () =>
      fermentations.map((f) => ({
        id: f.id,
        name: f.name ?? `F-${f.id}`,
        colorCode: f.color_code ? String(f.color_code) : null,
        status: f.status,
        startedAt: f.started_at,
        endedAt: f.ended_at,
        equipmentId: f.equipment_id,
      })),
    [fermentations],
  );

  // Set sidebar config
  useSidebarConfig({
    layer: 2,
    parent: { label: "Fermentations", icon: Milk, href: "/fermentations" },
    header: {
      title: group.label ? `${group.name} · ${group.label}` : group.name,
      subtitle: `${group.equipment_model_name ?? "Unknown"} • ${fermentations.length} tank${fermentations.length !== 1 ? "s" : ""}`,
      status: (
        <div className="flex gap-1 items-center w-full">
          <StatusBadge
            status={currentStatus}
            interactive={true}
            onTransition={(newStatus, force) => {
              if (newStatus === StatusEnum.ENDED) {
                setEndRunOpen(true);
                return;
              }
              // Starting is inoculation: the time, the starting protocol and
              // the feeds are one dialog on the inoculation page.
              if (newStatus === StatusEnum.RUNNING) {
                void navigate({
                  to: "/fermentations/$group_id/setup/inoculation",
                  params: { group_id: group.id },
                  search: { start: true },
                });
                return;
              }
              for (const ferm of fermentations) {
                onStatusTransition(ferm.id, newStatus, force);
              }
            }}
            transitionBlocked={transitionBlocked}
            blockReason={transitionBlockReason}
            size="sm"
          />
          {eft ? (
            <div className="flex-1 flex justify-end">
              <EftDisplay
                hours={eft.hours}
                timestamp={startedAt ? new Date(startedAt) : undefined}
                tooltipLabel="Started"
                size="sm"
                className="text-xs text-muted-foreground"
              />
            </div>
          ) : undefined}
        </div>
      ),
    },
    readout: (
      <>
        <LiveReadout variant="sidebar" tanks={readoutTanks} />
        <HealthSidebar groupId={group.id} />
        <TankControlLinks groupId={group.id} tanks={controlTanks} />
      </>
    ),
    navigation,
    variant: "complex",
  });

  return (
    <UnitPreferencesProvider unitPreferences={group.unit_preferences}>
      <main className="relative flex-1 min-h-0 overflow-auto h-full">
        <Outlet />
      </main>
      {endRunOpen && (
        <EndRunDialog
          group={group}
          open={endRunOpen}
          onOpenChange={setEndRunOpen}
          transition={(id, status, stopRun) =>
            transitionAsync(
              id,
              status,
              currentStatus !== StatusEnum.RUNNING,
              stopRun,
            )
          }
        />
      )}
    </UnitPreferencesProvider>
  );
}

interface EftResult {
  hours: number;
  isComplete: boolean;
  hasDataIssue: boolean;
  dataIssueMessage?: string;
}

function calculateEft(
  startedAt: Date,
  endedAt: Date | null | undefined,
  status: FermentationStatusEnum | null,
): EftResult {
  const isComplete =
    status === StatusEnum.ENDED || status === StatusEnum.ARCHIVED;

  let hasDataIssue = false;
  let dataIssueMessage: string | undefined;

  if (endedAt && endedAt.getTime() <= startedAt.getTime()) {
    hasDataIssue = true;
    dataIssueMessage = "Data issue: end time is before start time";
  }

  let endPoint: Date;
  if (isComplete && endedAt && !hasDataIssue) {
    endPoint = endedAt;
  } else {
    endPoint = new Date();
  }

  const diffMs = endPoint.getTime() - startedAt.getTime();
  const hours = diffMs / (1000 * 60 * 60);

  return {
    hours,
    isComplete: isComplete && !hasDataIssue,
    hasDataIssue,
    dataIssueMessage,
  };
}

function JournalSkeleton() {
  return (
    <div className="flex-1 p-4 space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}
